import { useSelector } from 'react-redux';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@client/components/ui/alert';
import { Skeleton } from '@client/components/ui/skeleton';
import { ContactSupportButton } from '@client/components/ContactSupportButton';
import { selectSession } from '@/model/selectors';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@client/components/ui/alert-dialog';

import { useSubscriptionSeats } from './useSubscriptionSeats';
import { useSeatInvitationAcceptDecline } from './useSeatInvitationAcceptDecline';
import {
  useSubscriptionViewManagement,
  type SubscriptionView,
} from './useSubscriptionViewManagement';
import { SubscriptionOverviewCard } from './SubscriptionOverviewCard';
import { NonOwnerOverviewCard } from './NonOwnerOverviewCard';
import { PendingSeatInvitationsSection } from './PendingSeatInvitationsSection';
import { AcceptSeatInvitationDialog } from './AcceptSeatInvitationDialog';

function LoadingSkeleton({ variant }: { variant: 'full' | 'compact' }) {
  if (variant === 'compact') {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4">
            <Skeleton className="h-6 w-48 rounded" />
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              <Skeleton className="h-12 w-28 rounded" />
              <Skeleton className="h-12 w-28 rounded" />
              <Skeleton className="h-12 w-28 rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-48 rounded" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-4">
            <div className="min-w-30">
              <Skeleton className="mb-2 h-4 w-20 rounded" />
              <Skeleton className="h-8 w-12 rounded" />
            </div>
            <div className="min-w-30">
              <Skeleton className="mb-2 h-4 w-16 rounded" />
              <Skeleton className="h-8 w-24 rounded" />
            </div>
            <div className="min-w-30">
              <Skeleton className="mb-2 h-4 w-28 rounded" />
              <Skeleton className="h-8 w-20 rounded" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ErrorState({ error }: { error: string }) {
  return (
    <Alert variant="destructive">
      <AlertDescription>
        <div className="space-y-4">
          <p>{error || 'Failed to load subscription data'}</p>
          <ContactSupportButton
            subject="Subscription Data Load Error"
            className="w-full sm:w-auto"
          />
        </div>
      </AlertDescription>
    </Alert>
  );
}

interface SubscriptionStatusContentProps {
  advisorId: string;
  variant?: 'full' | 'compact';
  hideManagementActions?: boolean;
  onInvitationAccepted?: () => void;
  onInvitationDeclined?: () => void;
}

export function SubscriptionStatusContent({
  advisorId,
  variant = 'full',
  hideManagementActions = false,
  onInvitationAccepted,
  onInvitationDeclined,
}: SubscriptionStatusContentProps) {
  const session = useSelector(selectSession);

  const {
    subscription,
    isOwner,
    creditBalance,
    unitPricePerSeat,
    activeSeatsCount,
    ownerInfo,
    currentAdvisorAssignedDateTime,
    currentAdvisorExpiresDateTime,
    isFetching,
    fetchError,
    openDialog,
  } = useSubscriptionSeats(advisorId);

  const {
    pendingSeatInvitations,
    acceptDialogOpen,
    invitationToAccept,
    declineDialogOpen,
    invitationToDecline,
    isAccepting,
    isDeclining,
    isProcessing,
    handleAccept,
    confirmAccept,
    cancelAccept,
    handleDecline,
    confirmDecline,
    cancelDecline,
  } = useSeatInvitationAcceptDecline({
    advisorId,
    onAcceptSuccess: onInvitationAccepted,
    onDeclineSuccess: onInvitationDeclined,
  });

  const isInvitationsLoading = pendingSeatInvitations === null;
  const hasPendingInvitations =
    pendingSeatInvitations !== null && pendingSeatInvitations.length > 0;

  const { view, setView } = useSubscriptionViewManagement({
    hasPendingInvitations,
    isFetching: isFetching || isInvitationsLoading,
  });

  if (isFetching || isInvitationsLoading) {
    return <LoadingSkeleton variant={variant} />;
  }

  if (fetchError) {
    return <ErrorState error={fetchError} />;
  }

  const containerClass = variant === 'compact' ? 'space-y-4' : 'space-y-6';

  return (
    <div className={containerClass}>
      {hasPendingInvitations && (
        <div className="mb-4">
          <div className="mb-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setView('invitations')}
              className={`text-sm font-medium ${
                view === 'invitations'
                  ? 'text-primary border-primary border-b-2 pb-1'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Invitations ({pendingSeatInvitations?.length ?? 0})
            </button>
            {subscription && (
              <button
                type="button"
                onClick={() => setView('subscription')}
                className={`text-sm font-medium ${
                  view === 'subscription'
                    ? 'text-primary border-primary border-b-2 pb-1'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Subscription
              </button>
            )}
          </div>
        </div>
      )}

      {view === 'invitations' && hasPendingInvitations && (
        <div>
          <p className="mb-3 text-sm text-gray-700">
            Review subscription invitations from other advisors who want you to join their
            subscription.
          </p>
          <PendingSeatInvitationsSection
            invitations={pendingSeatInvitations}
            onAccept={handleAccept}
            onDecline={handleDecline}
            isProcessing={isProcessing}
            decliningInvitationId={isDeclining ? invitationToDecline?.subscriptionSeatId : null}
          />
        </div>
      )}

      {view === 'subscription' && subscription && (
        <div>
          {isOwner ? (
            <SubscriptionOverviewCard
              subscription={subscription}
              activeSeatsCount={activeSeatsCount}
              creditBalance={creditBalance}
              unitPricePerSeat={unitPricePerSeat}
              isOwner={isOwner}
              onAddSeat={() => openDialog('addSeat')}
              hideManagementActions={hideManagementActions}
            />
          ) : (
            <NonOwnerOverviewCard
              ownerInfo={ownerInfo}
              currentAdvisorAssignedDateTime={currentAdvisorAssignedDateTime}
              currentAdvisorExpiresDateTime={currentAdvisorExpiresDateTime}
              seatStatus={session.stripe?.seatStatus}
            />
          )}
        </div>
      )}

      {!hasPendingInvitations && !subscription && (
        <Alert>
          <AlertDescription>
            <div className="space-y-4">
              <p>No subscription or pending invitations found.</p>
              <ContactSupportButton
                subject="Subscription Status Inquiry"
                className="w-full sm:w-auto"
              />
            </div>
          </AlertDescription>
        </Alert>
      )}

      <AcceptSeatInvitationDialog
        open={acceptDialogOpen}
        invitation={invitationToAccept}
        onAccept={confirmAccept}
        onDecline={cancelAccept}
        isSubmitting={isAccepting}
      />

      <AlertDialog open={declineDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              {`Are you sure you want to decline the subscription invitation from ${
                invitationToDecline?.ownerFirstName && invitationToDecline?.ownerLastName
                  ? `${invitationToDecline.ownerFirstName} ${invitationToDecline.ownerLastName}`
                  : invitationToDecline?.ownerEmail
              }? The subscription owner will be notified.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={cancelDecline}
              disabled={isDeclining}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-700 text-white hover:bg-red-800 disabled:opacity-50"
              onClick={confirmDecline}
              disabled={isDeclining}
            >
              {isDeclining ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Declining...
                </span>
              ) : (
                'Decline Invitation'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
